import React, { useState, useEffect } from "react";
import { X, Search } from "lucide-react";
import { useProducts } from "@/hooks/useInventory";
import { useCreateClientSupplement, useUpdateClientSupplement } from "@/hooks/useInventory";
import { ClientSupplementFormData, ClientSupplementWithDetails, ProductWithStock, getStockStatusColor } from "@/types/inventory";

type AssignSupplementModalProps = {
  clientId: string;
  appointmentId?: string;
  editingSupplement?: ClientSupplementWithDetails | null;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
};

export function AssignSupplementModal({ clientId, appointmentId, editingSupplement, onClose, onSuccess, onError }: AssignSupplementModalProps) {
  const isEditing = !!editingSupplement;

  const [formData, setFormData] = useState<ClientSupplementFormData>({
    product_id: "",
    quantity: 1,
    selling_price_per_unit: 0,
    dosage_instructions: "",
    duration_days: undefined,
    follow_up_date: "",
    notes: "",
    appointment_id: appointmentId,
    payment_option: appointmentId ? 'existing' : 'new',
    payment_due_date: "",
    create_refill_reminder: true,
    // Legacy support
    create_payment: true,
    create_reminder: true,
  });

  // Pre-fill form when editing
  useEffect(() => {
    if (editingSupplement) {
      setFormData({
        product_id: editingSupplement.product_id,
        quantity: editingSupplement.quantity,
        selling_price_per_unit: editingSupplement.selling_price_per_unit,
        dosage_instructions: editingSupplement.dosage_instructions || "",
        duration_days: editingSupplement.duration_days || undefined,
        follow_up_date: editingSupplement.follow_up_date || "",
        notes: editingSupplement.notes || "",
        appointment_id: editingSupplement.appointment_id || appointmentId,
        payment_option: 'none', // Don't create new payment when editing
        payment_due_date: "",
        create_refill_reminder: false, // Don't create new reminder when editing
        create_payment: false,
        create_reminder: false,
      });
    }
  }, [editingSupplement, appointmentId]);

  const [searchQuery, setSearchQuery] = useState("");
  const [showProductList, setShowProductList] = useState(false);

  const { data: productsData } = useProducts({ is_active: true });
  const createSupplement = useCreateClientSupplement(clientId);
  const updateSupplement = useUpdateClientSupplement(clientId);

  const products = productsData?.products || [];
  const selectedProduct = products.find((p) => p.id === formData.product_id);
  const isPending = createSupplement.isPending || updateSupplement.isPending;

  // Filter products based on search
  const filteredProducts = products.filter((product) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.brand?.toLowerCase().includes(searchLower) ||
      product.dosage?.toLowerCase().includes(searchLower)
    );
  });

  const handleSelectProduct = (product: ProductWithStock) => {
    setFormData({
      ...formData,
      product_id: product.id,
      selling_price_per_unit: product.default_selling_price || 0,
    });
    setShowProductList(false);
    setSearchQuery("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.product_id) {
      onError?.("Please select a product");
      return;
    }

    if (formData.quantity <= 0) {
      onError?.("Quantity must be greater than 0");
      return;
    }

    if (formData.selling_price_per_unit < 0) {
      onError?.("Price cannot be negative");
      return;
    }

    // Validate payment due date if payment is being created (only for new supplements)
    if (
      !isEditing &&
      formData.payment_option !== 'none' &&
      !formData.follow_up_date &&
      !formData.payment_due_date
    ) {
      onError?.("Please set either a follow-up date or payment due date");
      return;
    }

    // Check if enough stock (for new supplements or when increasing quantity)
    if (selectedProduct && !isEditing && selectedProduct.current_stock < formData.quantity) {
      onError?.(
        `Insufficient stock. Available: ${selectedProduct.current_stock}, Required: ${formData.quantity}`
      );
      return;
    }

    // When editing, check if we're increasing quantity and have enough stock
    if (isEditing && editingSupplement && selectedProduct) {
      const quantityIncrease = formData.quantity - editingSupplement.quantity;
      if (quantityIncrease > 0 && selectedProduct.current_stock < quantityIncrease) {
        onError?.(
          `Insufficient stock for increase. Available: ${selectedProduct.current_stock}, Needed: ${quantityIncrease}`
        );
        return;
      }
    }

    try {
      if (isEditing && editingSupplement) {
        // Update existing supplement
        await updateSupplement.mutateAsync({
          supplementId: editingSupplement.id,
          data: {
            quantity: formData.quantity,
            selling_price_per_unit: formData.selling_price_per_unit,
            dosage_instructions: formData.dosage_instructions || undefined,
            duration_days: formData.duration_days,
            follow_up_date: formData.follow_up_date || undefined,
            notes: formData.notes || undefined,
          },
        });
        onSuccess?.("Supplement updated successfully");
      } else {
        // Create new supplement
        await createSupplement.mutateAsync(formData);
        onSuccess?.("Supplement assigned successfully");
      }
    } catch (error: any) {
      onError?.(error.message || (isEditing ? "Failed to update supplement" : "Failed to assign supplement"));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? "Edit Supplement" : "Assign Supplement"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Product Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product *
            </label>
            {selectedProduct ? (
              <div className="border border-gray-300 rounded-lg p-3 flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">{selectedProduct.name}</p>
                  <p className="text-sm text-gray-500">
                    {[selectedProduct.brand, selectedProduct.dosage, selectedProduct.form]
                      .filter(Boolean)
                      .join(" • ")}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStockStatusColor(
                        selectedProduct.stock_status
                      )}`}
                    >
                      Stock: {selectedProduct.current_stock}
                    </span>
                  </div>
                </div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, product_id: "" });
                    }}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Change
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowProductList(true);
                    }}
                    onFocus={() => setShowProductList(true)}
                    placeholder="Search products..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                  />
                </div>

                {showProductList && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredProducts.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No products found
                      </div>
                    ) : (
                      filteredProducts.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => handleSelectProduct(product)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <p className="text-sm text-gray-500">
                            {[product.brand, product.dosage, product.form]
                              .filter(Boolean)
                              .join(" • ")}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStockStatusColor(
                                product.stock_status
                              )}`}
                            >
                              Stock: {product.current_stock}
                            </span>
                            {product.default_selling_price && (
                              <span className="text-xs text-gray-500">
                                ₹{product.default_selling_price}
                              </span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity *
            </label>
            <input
              type="number"
              min="1"
              value={formData.quantity}
              onChange={(e) =>
                setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              required
            />
          </div>

          {/* Selling Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selling Price per Unit (₹) *
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.selling_price_per_unit}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  selling_price_per_unit: parseFloat(e.target.value) || 0,
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              required
            />
            {formData.quantity > 0 && formData.selling_price_per_unit > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                Total: ₹{(formData.quantity * formData.selling_price_per_unit).toFixed(2)}
              </p>
            )}
          </div>

          {/* Dosage Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dosage Instructions
            </label>
            <textarea
              value={formData.dosage_instructions}
              onChange={(e) =>
                setFormData({ ...formData, dosage_instructions: e.target.value })
              }
              rows={3}
              placeholder="e.g., Take 1 capsule twice daily after meals"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration (days)
            </label>
            <input
              type="number"
              min="1"
              value={formData.duration_days || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  duration_days: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              placeholder="e.g., 30"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            />
          </div>

          {/* Follow-up Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Follow-up Date
            </label>
            <input
              type="date"
              value={formData.follow_up_date}
              onChange={(e) =>
                setFormData({ ...formData, follow_up_date: e.target.value })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
            />
          </div>

          {/* Payment Option Dropdown - Only show for new supplements */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment
              </label>
              <select
                value={formData.payment_option}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_option: e.target.value as 'new' | 'existing' | 'none',
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              >
                <option value="new">Create new payment</option>
                {appointmentId && (
                  <option value="existing">Add to appointment payment</option>
                )}
                <option value="none">No payment</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.payment_option === 'new' && 'Creates a separate payment for this supplement'}
                {formData.payment_option === 'existing' && 'Adds to existing appointment payment (or creates one if none exists)'}
                {formData.payment_option === 'none' && 'No payment will be created'}
              </p>
            </div>
          )}

          {/* Payment Due Date (conditional) - Only show for new supplements */}
          {!isEditing && formData.payment_option !== 'none' && !formData.follow_up_date && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Due Date *
              </label>
              <input
                type="date"
                value={formData.payment_due_date}
                onChange={(e) =>
                  setFormData({ ...formData, payment_due_date: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Required when no follow-up date is set
              </p>
            </div>
          )}

          {/* Re-fill Reminder Toggle - Only show for new supplements */}
          {!isEditing && formData.duration_days && formData.duration_days > 0 && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="create_refill_reminder"
                checked={formData.create_refill_reminder}
                onChange={(e) =>
                  setFormData({ ...formData, create_refill_reminder: e.target.checked })
                }
                className="w-4 h-4 text-[#5B8E87] border-gray-300 rounded focus:ring-[#5B8E87]"
              />
              <label htmlFor="create_refill_reminder" className="text-sm text-gray-700">
                Set re-fill reminder (auto-reminder after {formData.duration_days} days)
              </label>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex-1 px-4 py-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4A7069] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? (isEditing ? "Updating..." : "Assigning...")
                : (isEditing ? "Update Supplement" : "Assign Supplement")
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
